import { ResizeDirection } from '../types';
/**
 * Controls resizing behavior for widgets
 */
export class WidgetResizeController {
    constructor({ getResizeContext, applyChanges, throttleTime = 16, }) {
        Object.defineProperty(this, "isResizing", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "currentResizeDirection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ResizeDirection.NONE
        });
        Object.defineProperty(this, "initialMouseX", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "initialMouseY", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "initialPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { x: 0, y: 0 }
        });
        Object.defineProperty(this, "initialSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { width: 0, height: 0 }
        });
        Object.defineProperty(this, "lastUpdateTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "throttleTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "container", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "resizeHandles", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "handlers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "getResizeContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "applyChanges", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.getResizeContext = getResizeContext;
        this.applyChanges = applyChanges;
        this.throttleTime = throttleTime;
        this.handlers = {
            mouseMove: (e) => {
                if (!this.isResizing)
                    return;
                const now = performance.now();
                if (now - this.lastUpdateTime < this.throttleTime)
                    return;
                this.lastUpdateTime = now;
                e.preventDefault();
                // Calculate the new position and size using current mouse position
                const result = this.calculateResize({ x: e.clientX, y: e.clientY });
                // Apply the changes directly
                this.applyChanges(result.position, result.size);
            },
            mouseDown: (e) => {
                const target = e.target;
                const direction = this.getResizeDirectionFromHandle(target);
                if (direction === ResizeDirection.NONE)
                    return;
                // Get the initial context when resize starts
                const context = this.getResizeContext();
                this.initialPosition = { ...context.position };
                this.initialSize = { ...context.size };
                this.isResizing = true;
                this.currentResizeDirection = direction;
                // Store initial mouse position
                this.initialMouseX = e.clientX;
                this.initialMouseY = e.clientY;
                document.addEventListener('mousemove', this.handlers.mouseMove);
                document.addEventListener('mouseup', this.handlers.mouseUp);
                e.stopPropagation();
                e.preventDefault();
            },
            mouseUp: () => {
                if (!this.isResizing)
                    return;
                this.isResizing = false;
                this.currentResizeDirection = ResizeDirection.NONE;
                this.removeResizingListeners();
            },
        };
    }
    /**
     * Gets the resize direction from a handle element
     */
    getResizeDirectionFromHandle(element) {
        if (!element || !this.container)
            return ResizeDirection.NONE;
        for (const [direction, handle] of this.resizeHandles.entries()) {
            if (element === handle) {
                return direction;
            }
        }
        return ResizeDirection.NONE;
    }
    /**
     * Creates and sets up resize handles for the container
     */
    setupResizeHandles(container, getHandleStyles) {
        const directions = [
            ResizeDirection.N,
            ResizeDirection.E,
            ResizeDirection.S,
            ResizeDirection.W,
            ResizeDirection.NE,
            ResizeDirection.SE,
            ResizeDirection.SW,
            ResizeDirection.NW,
        ];
        directions.forEach((direction) => {
            const handle = document.createElement('div');
            handle.id = `${container.id}-resize-${direction}`;
            handle.style.cssText = getHandleStyles(direction);
            this.resizeHandles.set(direction, handle);
            container.appendChild(handle);
            handle.addEventListener('mousedown', this.handlers.mouseDown);
        });
    }
    removeResizingListeners() {
        document.removeEventListener('mousemove', this.handlers.mouseMove);
        document.removeEventListener('mouseup', this.handlers.mouseUp);
    }
    /**
     * Calculate resize dimensions and position based on direction and mouse position
     */
    calculateResize(mousePosition, minSize = { width: 200, height: 150 }) {
        if (!this.isResizing) {
            return {
                position: this.initialPosition,
                size: this.initialSize,
            };
        }
        // Current state
        let newLeft = this.initialPosition.x;
        let newTop = this.initialPosition.y;
        let newWidth = this.initialSize.width;
        let newHeight = this.initialSize.height;
        // Calculate deltas
        const deltaX = mousePosition.x - this.initialMouseX;
        const deltaY = mousePosition.y - this.initialMouseY;
        // Apply resize changes based on direction
        switch (this.currentResizeDirection) {
            case ResizeDirection.E:
                newWidth = Math.max(this.initialSize.width + deltaX, minSize.width);
                break;
            case ResizeDirection.S:
                newHeight = Math.max(this.initialSize.height + deltaY, minSize.height);
                break;
            case ResizeDirection.W:
                newWidth = Math.max(this.initialSize.width - deltaX, minSize.width);
                if (newWidth !== this.initialSize.width) {
                    newLeft = this.initialPosition.x + deltaX;
                }
                break;
            case ResizeDirection.N:
                newHeight = Math.max(this.initialSize.height - deltaY, minSize.height);
                if (newHeight !== this.initialSize.height) {
                    newTop = this.initialPosition.y + deltaY;
                }
                break;
            case ResizeDirection.SE:
                newWidth = Math.max(this.initialSize.width + deltaX, minSize.width);
                newHeight = Math.max(this.initialSize.height + deltaY, minSize.height);
                break;
            case ResizeDirection.SW:
                newWidth = Math.max(this.initialSize.width - deltaX, minSize.width);
                newHeight = Math.max(this.initialSize.height + deltaY, minSize.height);
                if (newWidth !== this.initialSize.width) {
                    newLeft = this.initialPosition.x + deltaX;
                }
                break;
            case ResizeDirection.NE:
                newWidth = Math.max(this.initialSize.width + deltaX, minSize.width);
                newHeight = Math.max(this.initialSize.height - deltaY, minSize.height);
                if (newHeight !== this.initialSize.height) {
                    newTop = this.initialPosition.y + deltaY;
                }
                break;
            case ResizeDirection.NW:
                newWidth = Math.max(this.initialSize.width - deltaX, minSize.width);
                newHeight = Math.max(this.initialSize.height - deltaY, minSize.height);
                if (newWidth !== this.initialSize.width) {
                    newLeft = this.initialPosition.x + deltaX;
                }
                if (newHeight !== this.initialSize.height) {
                    newTop = this.initialPosition.y + deltaY;
                }
                break;
        }
        return {
            position: { x: newLeft, y: newTop },
            size: { width: newWidth, height: newHeight },
        };
    }
    /**
     * Initializes the controller with necessary DOM elements
     */
    initialize(container, getHandleStyles) {
        this.container = container;
        // Set up the resize handles
        this.setupResizeHandles(container, getHandleStyles);
    }
    /**
     * Checks if resizing is active
     */
    isResizingActive() {
        return this.isResizing;
    }
    /**
     * Gets the current resize direction
     */
    getCurrentResizeDirection() {
        return this.currentResizeDirection;
    }
    /**
     * Gets the initial mouse position (where resize started)
     */
    getInitialMousePosition() {
        return { x: this.initialMouseX, y: this.initialMouseY };
    }
    /**
     * Cleans up the controller
     */
    cleanup() {
        this.removeResizingListeners();
        // Clean up resize handles
        if (this.resizeHandles.size > 0) {
            this.resizeHandles.forEach((handle) => {
                if (handle.parentNode) {
                    handle.parentNode.removeChild(handle);
                }
                // Remove all event listeners
                handle.replaceWith(handle.cloneNode(false));
            });
            this.resizeHandles.clear();
        }
        this.container = null;
        this.isResizing = false;
        this.currentResizeDirection = ResizeDirection.NONE;
    }
}
//# sourceMappingURL=WidgetResizeController.js.map